<template>
  <div
    v-if="!$vuefront.isAMP"
    :class="`vf-a-image image-wrapper layout-${layout}`"
    :style="styles"
  >
    <div
      class="vf-a-image__sizer image-sizer"
      :style="{ paddingBottom: aspectRatio + '%' }"
    ></div>
    <picture
      v-if="renderedImage"
      class="vf-a-image__picture"
      style="display: contents"
    >
      <!-- Responsive AVIF/WebP sources: only present when the caller passes a
           rendition srcset (i.e. the API image is a base .avif). Absent for
           raster-only products, so this degrades to a plain image. -->
      <source
        v-if="srcset"
        type="image/avif"
        :srcset="srcset"
        :sizes="sizes || undefined"
      />
      <source
        v-if="webpSrc"
        type="image/webp"
        :srcset="webpSrc"
        :sizes="sizes || undefined"
      />
      <img
        :srcset="renderedImgSrcset || undefined"
        :sizes="renderedImgSrcset && sizes ? sizes : undefined"
        :src="renderedImage"
        :alt="alt"
        :title="title"
        :width="intrinsicSize ? intrinsicSize.w : null"
        :height="intrinsicSize ? intrinsicSize.h : null"
        :loading="eager ? 'eager' : 'lazy'"
        :fetchpriority="eager ? 'high' : 'auto'"
        decoding="async"
        class="vf-a-image__img image-img"
        :class="imageClass"
        :style="imageStyles"
        @load="handleLoad"
        @error="handleError"
      />
    </picture>
    <div class="vf-a-image__content" :class="contentClass">
      <slot></slot>
    </div>
  </div>
  <img
    v-else
    v-lazy="src"
    :alt="alt"
    :title="title"
    :width="widthAmp"
    :height="heightAmp"
    :heights="heights"
    :layout="layout"
  />
</template>
<script lang="ts" setup>
import { PropType, computed, inject, ref, watch } from "vue";
const vuefront$ = inject<any>("$vuefront");

const emit = defineEmits(["load", "error"]);

const props = defineProps({
  contentClass: {
    type: String,
    default: () => "",
  },
  blankColor: {
    type: String,
    default() {
      return null;
    },
  },
  lazySrc: {
    validator() {
      return true;
    },
    default() {
      return "";
    },
  },
  src: {
    validator() {
      return true;
    },
    default() {
      return "";
    },
  },
  srcDark: {
    validator() {
      return true;
    },
    default() {
      return "";
    },
  },
  lazySrcDark: {
    validator() {
      return true;
    },
    default() {
      return "";
    },
  },
  width: {
    type: [String, Number] as PropType<string | number>,
    default() {
      return "";
    },
  },
  alt: {
    type: String,
    default() {
      return "";
    },
  },
  title: {
    type: String,
    default() {
      return "";
    },
  },
  layout: {
    type: String,
    default() {
      return "responsive";
    },
  },
  widthAmp: {
    type: [String, Number] as PropType<string | number>,
    default() {
      return "1";
    },
  },
  heightAmp: {
    type: [String, Number] as PropType<string | number>,
    default() {
      return "1";
    },
  },
  heights: {
    type: String,
    default() {
      return null;
    },
  },
  cover: {
    type: Boolean,
    default() {
      return false;
    },
  },
  eager: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // Responsive image support (optional). When `srcset` is provided the atom
  // renders a <picture> with an AVIF <source> (and a WebP one if `webpSrc` is
  // set); otherwise it renders a plain <img> exactly as before.
  srcset: {
    type: String,
    default() {
      return "";
    },
  },
  webpSrc: {
    type: String,
    default() {
      return "";
    },
  },
  // Optional srcset of the <img> itself (the universally decodable fallback
  // under the typed sources, e.g. the JPEG of a product photo's `media`);
  // it takes `sizes` too. Absent, the <img> has only `src`, as before.
  imgSrcset: {
    type: String,
    default() {
      return "";
    },
  },
  sizes: {
    type: String,
    default() {
      return "";
    },
  },
});

const failedSrc = ref("");

const aspectRatio = computed(() => {
  return 100 / (+props.widthAmp / +props.heightAmp);
});

// A5: expose the intrinsic dimensions to the browser as real width/height
// attributes so it knows the aspect ratio before CSS/JS run. The image is
// absolutely positioned at 100%×100% over the padding-bottom sizer, so these
// attributes only inform the aspect ratio — they never change rendered size.
// Guarded to genuine pixel dimensions (the AMP defaults are "1"×"1").
const intrinsicSize = computed<{ w: number; h: number } | null>(() => {
  const w = Number(props.widthAmp);
  const h = Number(props.heightAmp);
  return Number.isFinite(w) && Number.isFinite(h) && w > 1 && h > 1
    ? { w, h }
    : null;
});

const styles = computed<{ width?: string; height?: string }>(() => {
  const styles: { width?: string; height?: string } = {};

  if (props.width !== "") {
    styles.width = props.width + "px";
  }

  if (props.layout === "flex-item") {
    styles.width = props.widthAmp + "px";
    styles.height = props.heightAmp + "px";
  }

  return styles;
});

const isDark = computed(() => {
  let theme = false;
  if (vuefront$.isClient) {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      theme = true;
    }
  }

  return theme;
});

const image = computed(() => {
  let result = props.src;
  if (isDark.value && props.srcDark !== "") {
    result = props.srcDark;
  }

  return result;
});

const lazyImage = computed(() => {
  let result = props.lazySrc;

  if (isDark.value && props.lazySrcDark !== "") {
    result = props.lazySrcDark;
  }

  return result;
});

const renderedImage = computed(() => {
  if (failedSrc.value && failedSrc.value === image.value) {
    return lazyImage.value && lazyImage.value !== image.value
      ? lazyImage.value
      : "";
  }

  return image.value || lazyImage.value;
});

// The <img> srcset belongs to `src`: once that photo failed, the fallback
// shows on its own.
const renderedImgSrcset = computed(() =>
  failedSrc.value && failedSrc.value === image.value ? "" : props.imgSrcset
);

const imageStyles = computed(() => {
  const styles: { backgroundColor?: string } = {};

  if (props.blankColor) {
    styles.backgroundColor = props.blankColor;
  }

  return styles;
});

watch(image, () => {
  failedSrc.value = "";
});

const imageClass = computed(() => {
  let result = "";

  if (props.cover) {
    result += "vf-a-image__img--cover";
  }
  return result;
});

function handleLoad(event: Event) {
  emit("load", event);
}

function handleError(event: Event) {
  failedSrc.value = image.value;
  emit("error", event);
}
</script>
